/**
 * The signed-out landing's program document, requested by the inline shell in index.html at HTML
 * parse time (load-speed work 2026-09-14). Before, the first request left only after the entry
 * bundle, the router's ParticipantGuide chunk graph and its setup had run - 10.6 s into a Slow 4G
 * load - and App.vue's unauth watcher sent an identical second one in the same tick.
 *
 * The shell leaves `window.__pcvProgram = { id, at, promise }` (promise -> the document or null).
 * `getProgramByNameOrIdOrSharableUrl` takes it for a `by_id` request of the same id within a minute,
 * and concurrent identical requests share one in-flight promise. Pure lookup; never throws.
 */
export interface ProgramPrefetch {
  id: string;
  at: number;
  promise: Promise<unknown>;
}

export const PREFETCH_MAX_AGE_MS = 60 * 1000;

export function prefetchedProgram(
  data: { type?: string; program_id?: string } | null | undefined,
  now: number = Date.now(),
  holder: { __pcvProgram?: ProgramPrefetch } = window as unknown as {
    __pcvProgram?: ProgramPrefetch;
  }
): Promise<unknown> | null {
  const p = holder?.__pcvProgram;
  if (!p || !data || data.type !== 'by_id' || !data.program_id) return null;
  if (p.id !== data.program_id || now - p.at > PREFETCH_MAX_AGE_MS) return null;
  return p.promise;
}

const inFlight = new Map<string, Promise<unknown>>();

/** One request per URL while it is pending; later calls after it settles fetch fresh. */
export function shareInFlight<T>(key: string, start: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const p = start().finally(() => inFlight.delete(key));
  inFlight.set(key, p);
  return p;
}
